import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import { cyan } from '@mui/material/colors'
import { useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import React from 'react'

import { ReactComponent as BinCleaningIcon } from 'assets/service-action-types/BinCleaningIcon.svg'
import { ReactComponent as CollectionIcon } from 'assets/service-action-types/CollectionIcon.svg'
import { ReactComponent as ConciergeIcon } from 'assets/service-action-types/ConciergeIcon.svg'
import { ReactComponent as CourtesyIcon } from 'assets/service-action-types/CourtesyIcon.svg'
import { ReactComponent as DeliveryIcon } from 'assets/service-action-types/DeliveryIcon.svg'
import { ReactComponent as DropIcon } from 'assets/service-action-types/DropIcon.svg'
import { ReactComponent as DryRunIcon } from 'assets/service-action-types/DryRunIcon.svg'
import { ReactComponent as DumpRelocateIcon } from 'assets/service-action-types/DumpRelocateIcon.svg'
import { ReactComponent as DumpReturnIcon } from 'assets/service-action-types/DumpReturnIcon.svg'
import { ReactComponent as FinalIcon } from 'assets/service-action-types/FinalIcon.svg'
import { ReactComponent as InstallIcon } from 'assets/service-action-types/InstallIcon.svg'
import { ReactComponent as LiveLoadIcon } from 'assets/service-action-types/LiveLoadIcon.svg'
import { ReactComponent as PumpIcon } from 'assets/service-action-types/PumpIcon.svg'
import { ReactComponent as RentalIcon } from 'assets/service-action-types/RentalIcon.svg'
import { ReactComponent as RepairIcon } from 'assets/service-action-types/RepairIcon.svg'
import { ReactComponent as RepositionIcon } from 'assets/service-action-types/RepositionIcon.svg'
import { ReactComponent as SalesIcon } from 'assets/service-action-types/SalesIcon.svg'
import { ReactComponent as SwapIcon } from 'assets/service-action-types/SwapIcon.svg'
import { SERVICE_ACTION_TYPE, SERVICE_ACTION_TYPE_TO_COLOR_MAP } from 'settings/constants/service'
import T from 'T'
import { ALPHA_38_PERCENT } from 'theme/colors'
import { normalizeCasing } from 'utils/string'

import type { ChipProps } from '@mui/material/Chip'

export const SERVICE_ACTION_TYPE_TO_SVG: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  SWAP: SwapIcon,
  DELIVERY: DeliveryIcon,
  'DUMP & RETURN': DumpReturnIcon,
  DROP: DropIcon,
  'LIVE LOAD': LiveLoadIcon,
  REPOSITION: RepositionIcon,
  FINAL: FinalIcon,
  COLLECTION: CollectionIcon,
  RENTAL: RentalIcon,
  CONCIERGE: ConciergeIcon,
  REPAIR: RepairIcon,
  'DUMP & RELOCATE': DumpRelocateIcon,
  COURTESY: CourtesyIcon,
  INSTALL: InstallIcon,
  'BIN CLEANING': BinCleaningIcon,
  SALES: SalesIcon,
  PUMP: PumpIcon,
  'DRY RUN': DryRunIcon,
  'RECURRING COLLECTION': CollectionIcon,
}

type Props = Omit<ChipProps, 'label'> & {
  action?: string | null
  label?: string | null
}

const ServiceActionTypeChip: React.FC<Props> = ({ action, label, clickable = false, ...rest }) => {
  const isRecurringCollection = label === T.RECURRING_COLLECTION
  const theme = useTheme()
  const actionUpperCase = action?.toUpperCase() ?? ''
  const ActionTypeIcon = SERVICE_ACTION_TYPE_TO_SVG[actionUpperCase]
  const backgroundColor =
    actionUpperCase in SERVICE_ACTION_TYPE
      ? SERVICE_ACTION_TYPE_TO_COLOR_MAP[actionUpperCase as keyof typeof SERVICE_ACTION_TYPE_TO_COLOR_MAP]
      : undefined
  const isMediumSize = (rest.size ?? 'medium') === 'medium'
  const isOutlined = rest.variant === 'outlined'

  if (!action) return null
  return (
    <Chip
      avatar={
        ActionTypeIcon ? (
          <Avatar sx={{ backgroundColor: `${theme.palette.background.black}${ALPHA_38_PERCENT}` }}>
            <SvgIcon inheritViewBox sx={{ fontSize: isMediumSize ? '1.1rem' : 14, color: theme.palette.common.white }}>
              <ActionTypeIcon />
            </SvgIcon>
          </Avatar>
        ) : undefined
      }
      clickable={clickable}
      label={label || normalizeCasing(action)}
      sx={{
        ...(!isOutlined && {
          backgroundColor: isRecurringCollection ? cyan[100] : backgroundColor?.[100],
          ...(clickable && {
            '&:hover': {
              backgroundColor: isRecurringCollection ? cyan[200] : backgroundColor?.[200],
            },
          }),
        }),
      }}
      {...rest}
    />
  )
}

export default ServiceActionTypeChip
